import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../../../store/index';
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Brightness1Icon from '@material-ui/icons/Brightness1';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useStyles } from '../ExaminationTable/ExaminationTable.style';
import { formatSimpleDateHour } from '../../../../../helpers/formatDate';
import { getColumnCellStyle } from '../examTableColumnWidths';
import { formatPatientName } from '../../../../../helpers/misc';
import EventIcon from '@material-ui/icons/Event';
import UpdateIcon from '@material-ui/icons/Update';

export const hasWorklistNestedExams = (patient) =>
  Boolean(patient.prior_study_id && patient.prior_study_date);

export const buildWorklistRowItems = (patient) => {
  if (!hasWorklistNestedExams(patient)) {
    return [{ ...patient, is_prior: false }];
  }

  return [
    { ...patient, is_prior: false },
    { ...patient, is_prior: true },
  ];
};

function WorklistTableRows({ patients, tableColumns }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();

  const { localChangesDensity, localChangesQuality, localChangesMicrocalc, localChangesOpacities } =
    useSelector((state) => state.labeling);
  const { currentExam } = useSelector((state) => state.examinations);

  const [expandedRows, setExpandedRows] = useState({});

  const someChanges =
    localChangesDensity || localChangesQuality || localChangesMicrocalc || localChangesOpacities;

  useEffect(() => {
    patients.forEach((patient) => {
      if (!hasWorklistNestedExams(patient)) return;

      const rowKey = patient.study_instance_uid || patient.patient_id;
      const matchesCurrent =
        currentExam === patient.study_instance_uid || currentExam === patient.prior_study_id;

      if (matchesCurrent) {
        setExpandedRows((prev) => ({ ...prev, [rowKey]: true }));
      }
    });
  }, [patients, currentExam]);

  const handleRowClick = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const handlePatientClick = (studyId) => {
    if (studyId) {
      if (!someChanges) {
        dispatch(actions.showExamination(studyId));
      } else {
        dispatch(actions.setActiveDialog('unsavedChanges'));
      }
      return;
    }
  };

  const statusIconInfo = (status) => {
    switch (status) {
      case 'new':
        return { color: '#949494', tooltip: t('Table.new') };
      case 'prior_fetched':
        return { color: '#aaaa68', tooltip: t('Table.prior_fetched') };
      case 'no_priors':
        return { color: '#679fa4', tooltip: t('Table.no_priors') };
      case 'report_sent':
        return { color: '#52754c', tooltip: t('Table.report_sent') };
      case 'error':
        return { color: '#e83838', tooltip: t('Table.error') };
      default:
        return { color: '#FF0000', tooltip: t('Table.status') };
    }
  };

  const nestedPatientSelected = (patient) =>
    currentExam === patient.study_instance_uid || currentExam === patient.prior_study_id;

  const renderStatusIcon = (patient) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Tooltip
        title={
          <Typography className={classes.tooltip_texts}>
            {statusIconInfo(patient.status)?.tooltip || 'status'}
          </Typography>
        }
        arrow
        placement='top-start'>
        <Brightness1Icon
          fontSize='small'
          style={{
            color: statusIconInfo(patient.status)?.color,
            maxHeight: '11px',
            maxWidth: '11px',
            cursor: 'help',
          }}
        />
      </Tooltip>
    </div>
  );

  const renderDateCell = (date, isPrior, isSelected) => {
    const formatted = formatSimpleDateHour(date);

    return (
      <Typography className={isSelected ? classes.row_selected : classes.table_date_font}>
        {date
          ? isPrior
            ? formatted.date
            : `${formatted.date} ${formatted.hour ? `- ${formatted.hour}` : ''}`
          : '-'}
      </Typography>
    );
  };

  const renderNestedCell = (column, item, isSelected) => {
    const date = item.is_prior ? item.prior_study_date : item.scheduled_date;

    switch (column.key) {
      case 'status':
        return null;
      case 'expand':
        return null;
      case 'patient_name':
        return (
          <>
            {item.is_prior ? (
              <Tooltip
                arrow
                placement='right-start'
                title={
                  <Typography className={classes.tooltip_texts}>
                    {t('Table.previous_examination', {
                      patient_name: item.patient_name,
                      prior_study_date: formatSimpleDateHour(date).date,
                    })}
                  </Typography>
                }>
                <Typography
                  className={isSelected ? classes.row_selected : classes.table_font}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <UpdateIcon
                    fontSize='small'
                    style={{ height: '14px', width: '14px', transform: 'rotateY(180deg)' }}
                  />{' '}
                  <span>{t('Table.prior_nested_exam')}</span>
                </Typography>
              </Tooltip>
            ) : (
              <Tooltip
                arrow
                placement='right-start'
                title={
                  <Typography className={classes.tooltip_texts}>
                    {t('Table.scheduled_exam', {
                      patient_name: item.patient_name,
                      scheduled_date: formatSimpleDateHour(date).date,
                    })}
                  </Typography>
                }>
                <Typography
                  className={isSelected ? classes.row_selected : classes.table_font}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <EventIcon fontSize='small' style={{ height: '14px', width: '14px' }} />{' '}
                  <span>{t('Table.scheduled_nested_exam')}</span>
                </Typography>
              </Tooltip>
            )}
          </>
        );
      case 'patient_birth_date':
      case 'patient_id':
        return null;
      case 'scheduled_date':
        return renderDateCell(date, item.is_prior, isSelected);
      default:
        return null;
    }
  };

  const renderParentCell = (column, patient, rowItems, isRowExpanded, isSelected) => {
    const hasNested = rowItems.length > 1;

    switch (column.key) {
      case 'status':
        return renderStatusIcon(patient);
      case 'expand':
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton
              aria-label='expand row'
              size='small'
              style={{
                width: '22px',
                height: '22px',
                minWidth: '22px',
                maxWidth: '22px',
                padding: 0,
              }}>
              {hasNested &&
                (isRowExpanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />)}
            </IconButton>
          </div>
        );
      case 'patient_name':
        return (
          <Tooltip
            arrow
            placement='top'
            title={
              <Typography color='inherit' className={classes.tooltip_texts}>
                {t('Table.no_previous_examination_found')}
              </Typography>
            }>
            <Typography className={isSelected ? classes.row_selected : classes.table_font}>
              {formatPatientName(patient?.patient_name || '')}
            </Typography>
          </Tooltip>
        );
      case 'patient_birth_date':
        return (
          <Typography className={classes.table_date_font}>
            {patient.patient_birth_date || patient.birth_date
              ? formatSimpleDateHour(patient.patient_birth_date || patient.birth_date).date
              : '-'}
          </Typography>
        );
      case 'patient_id':
        return <Typography className={classes.table_date_font}>{patient.patient_id || '-'}</Typography>;
      case 'scheduled_date':
        if (hasNested && isRowExpanded) return null;
        return renderDateCell(patient.scheduled_date, false, isSelected);
      default:
        return <Typography className={classes.table_font}>{patient[column.key]}</Typography>;
    }
  };

  const nestedTable = (item, rowKey, index) => {
    const isSelected = item.is_prior && currentExam === item.prior_study_id;

    return (
      <TableRow
        hover
        key={`${rowKey}-nested-${index}`}
        className={isSelected ? classes.tr_nested_hover_selected : classes.tr_nested_hover}
        selected={isSelected}
        style={{
          cursor: item.is_prior ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (item.is_prior) handlePatientClick(item.prior_study_id);
        }}>
        {tableColumns.map((column) => (
          <TableCell
            key={column.key}
            padding='none'
            className={classes.tr_nested}
            style={getColumnCellStyle(column.width, {
              noPadding: column.key === 'status' || column.key === 'expand',
            })}>
            {renderNestedCell(column, item, isSelected)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <TableBody>
      {patients.map((patient, index) => {
        const rowKey = patient.study_instance_uid || patient.patient_id || `${patient.patient_name}-${index}`;
        const rowItems = buildWorklistRowItems(patient);
        const hasNested = rowItems.length > 1;
        const isRowExpanded = expandedRows[rowKey] || false;
        const isSelected =
          currentExam === patient.study_instance_uid || (hasNested && nestedPatientSelected(patient));

        return (
          <React.Fragment key={rowKey}>
            <TableRow
              hover
              className={hasNested ? classes.tr_hover : undefined}
              style={{
                cursor: hasNested ? 'pointer' : undefined,
                backgroundColor: isSelected
                  ? 'rgb(84 84 84)'
                  : isRowExpanded
                    ? theme.palette.background.highlightLight
                    : 'transparent',
              }}
              onClick={() => hasNested && handleRowClick(rowKey)}>
              {tableColumns.map((column) => (
                <TableCell
                  key={column.key}
                  padding='none'
                  className={
                    column.key === 'status' || column.key === 'expand'
                      ? classes.worklist_tr_padding
                      : classes.worklist_table_cell_container
                  }
                  style={getColumnCellStyle(column.width, {
                    noPadding: column.key === 'status' || column.key === 'expand',
                  })}>
                  {renderParentCell(column, patient, rowItems, isRowExpanded, isSelected)}
                </TableCell>
              ))}
            </TableRow>

            {hasNested && (
              <TableRow>
                <TableCell
                  style={{
                    padding: 0,
                    backgroundColor: theme.palette.background.highlightDarker,
                  }}
                  colSpan={tableColumns.length}>
                  <Collapse in={isRowExpanded} timeout='auto' unmountOnExit>
                    <Table size='small' aria-label='worklist-nested-table'>
                      <TableBody>{rowItems.map((item, i) => nestedTable(item, rowKey, i))}</TableBody>
                    </Table>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
}

export default WorklistTableRows;
